import apiClient from '../api';
import { handleError } from '../utils/errorHandler';

const ENDPOINT_PREFIX = '/transport';

// Exportamos la URL base para el proxy de imágenes
export const API_BASE_URL = apiClient.defaults.baseURL || '';

export { apiClient };

// --- Vehicle Types ---
export const getVehicleTypes = async () => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/vehicle-types/`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar tipos de vehículos.");
        throw error;
    }
};

export const createVehicleType = async (vehicleType) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/vehicle-types/`, vehicleType);
        return response.data;
    } catch (error) {
        handleError(error, "Error al crear tipo de vehículo.");
        throw error;
    }
};

export const updateVehicleType = async (id, vehicleType) => {
    try {
        const response = await apiClient.put(`${ENDPOINT_PREFIX}/vehicle-types/${id}`, vehicleType);
        return response.data;
    } catch (error) {
        handleError(error, "Error al actualizar tipo de vehículo.");
        throw error;
    }
};

// --- Clients ---
export const getClients = async () => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/clients/`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar clientes.");
        throw error;
    }
};

export const createClient = async (client) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/clients/`, client);
        return response.data;
    } catch (error) {
        handleError(error, "Error al crear cliente.");
        throw error;
    }
};

export const updateClient = async (id, client) => {
    try {
        const response = await apiClient.put(`${ENDPOINT_PREFIX}/clients/${id}`, client);
        return response.data;
    } catch (error) {
        handleError(error, "Error al actualizar cliente.");
        throw error;
    }
};

// --- Drivers ---
export const getDrivers = async (params = {}) => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/drivers/`, { params });
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar conductores.");
        throw error;
    }
};

export const createDriver = async (driver) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/drivers/`, driver);
        return response.data;
    } catch (error) {
        handleError(error, "Error al crear conductor.");
        throw error;
    }
};

export const updateDriver = async (id, driver) => {
    try {
        const response = await apiClient.put(`${ENDPOINT_PREFIX}/drivers/${id}`, driver);
        return response.data;
    } catch (error) {
        handleError(error, "Error al actualizar conductor.");
        throw error;
    }
};

export const updateDriverStatus = async (driverId, status) => {
    try {
        const response = await apiClient.put(`${ENDPOINT_PREFIX}/drivers/${driverId}/status`, null, {
            params: { status }
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al actualizar estado del conductor.");
        throw error;
    }
};

export const closeDriverSession = async (driverId) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/drivers/${driverId}/close-session`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al cerrar la sesión del conductor.");
        throw error;
    }
};

export const openDriverSession = async (driverId) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/drivers/${driverId}/open-session`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al iniciar la sesión.");
        throw error;
    }
};


// --- Trips ---
export const getTrips = async (filters = {}) => {
    try {
        // Aseguramos que vehicle_code se envíe tal cual al backend
        const params = { ...filters };
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/trips/`, { params });
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar viajes.");
        throw error;
    }
};

export const globalSearchTrips = async (filters = {}) => {
    try {
        const params = { ...filters };
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/trips/global-search/`, { params });
        return response.data;
    } catch (error) {
        handleError(error, "Error al buscar viajes en el buscador global.");
        throw error;
    }
};

export const createTrip = async (trip) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/trips/`, trip);
        return response.data;
    } catch (error) {
        handleError(error, "Error al crear viaje.");
        throw error;
    }
};

export const updateTrip = async (tripId, tripUpdate) => {
    try {
        const response = await apiClient.put(`${ENDPOINT_PREFIX}/trips/${tripId}`, tripUpdate);
        return response.data;
    } catch (error) {
        // Permitimos que el componente maneje el error para mostrar mensajes específicos (ej: Panel Admin)
        throw error;
    }
};

export const voidTrip = async (tripId, reason) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/trips/${tripId}/void`, { reason });
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const unvoidTrip = async (tripId) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/trips/${tripId}/unvoid`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const requesterCancelTrip = async (tripId) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/trips/${tripId}/requester-cancel`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// --- Areas ---
export const getAreas = async (clientId) => {
    try {
        const params = clientId ? { client_id: clientId } : {};
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/areas/`, { params });
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar áreas.");
        throw error;
    }
};

export const createArea = async (area) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/areas/`, area);
        return response.data;
    } catch (error) {
        handleError(error, "Error al crear área.");
        throw error;
    }
};

export const updateArea = async (id, area) => {
    try {
        const response = await apiClient.put(`${ENDPOINT_PREFIX}/areas/${id}`, area);
        return response.data;
    } catch (error) {
        handleError(error, "Error al actualizar área.");
        throw error;
    }
};

export const deleteArea = async (id) => {
    try {
        const response = await apiClient.delete(`${ENDPOINT_PREFIX}/areas/${id}`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al eliminar área.");
        throw error;
    }
};

// --- Cost Centers ---
export const getCostCenters = async (clientId) => {
    try {
        const params = clientId ? { client_id: clientId } : {};
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/cost-centers/`, { params });
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar centros de costo.");
        throw error;
    }
};

export const createCostCenter = async (cc) => {
    try {
        const sanitized = {
            ...cc,
            code: cc.code ? cc.code.trim() : cc.code,
            file: cc.file ? cc.file.trim() : cc.file
        };
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/cost-centers/`, sanitized);
        return response.data;
    } catch (error) {
        handleError(error, "Error al crear centro de costo.");
        throw error;
    }
};

export const updateCostCenter = async (id, cc) => {
    try {
        const sanitized = {
            ...cc,
            code: cc.code ? cc.code.trim() : cc.code,
            file: cc.file ? cc.file.trim() : cc.file
        };
        const response = await apiClient.put(`${ENDPOINT_PREFIX}/cost-centers/${id}`, sanitized);
        return response.data;
    } catch (error) {
        handleError(error, "Error al actualizar centro de costo.");
        throw error;
    }
};

export const deleteCostCenter = async (id) => {
    try {
        const response = await apiClient.delete(`${ENDPOINT_PREFIX}/cost-centers/${id}`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al eliminar centro de costo.");
        throw error;
    }
};

// --- Passengers ---
export const getPassengers = async (clientId) => {
    try {
        const params = clientId ? { client_id: clientId } : {};
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/passengers/`, { params });
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar pasajeros.");
        throw error;
    }
};

export const createPassenger = async (passenger) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/passengers/`, passenger);
        return response.data;
    } catch (error) {
        handleError(error, "Error al crear pasajero.");
        throw error;
    }
};

export const updatePassenger = async (id, passenger) => {
    try {
        const response = await apiClient.put(`${ENDPOINT_PREFIX}/passengers/${id}`, passenger);
        return response.data;
    } catch (error) {
        handleError(error, "Error al actualizar pasajero.");
        throw error;
    }
};

export const deletePassenger = async (id) => {
    try {
        const response = await apiClient.delete(`${ENDPOINT_PREFIX}/passengers/${id}`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al eliminar pasajero.");
        throw error;
    }
};

// --- Requesters ---
export const getRequesters = async (clientIdOrParams) => {
    try {
        let params = {};
        if (clientIdOrParams && typeof clientIdOrParams === 'object') {
            params = clientIdOrParams;
        } else if (clientIdOrParams) {
            params = { client_id: clientIdOrParams };
        }
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/requesters/`, { params });
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar solicitantes.");
        throw error;
    }
};

export const createRequester = async (requester) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/requesters/`, requester);
        return response.data;
    } catch (error) {
        handleError(error, "Error al crear solicitante.");
        throw error;
    }
};

export const updateRequester = async (id, requester) => {
    try {
        const response = await apiClient.put(`${ENDPOINT_PREFIX}/requesters/${id}`, requester);
        return response.data;
    } catch (error) {
        handleError(error, "Error al actualizar solicitante.");
        throw error;
    }
};

export const deleteRequester = async (id) => {
    try {
        const response = await apiClient.delete(`${ENDPOINT_PREFIX}/requesters/${id}`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al eliminar solicitante.");
        throw error;
    }
};

// --- Waiting Rates ---
export const getWaitingRates = async (clientId) => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/clients/${clientId}/waiting-rates`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar tarifas de espera.");
        throw error;
    }
};

export const createWaitingRate = async (rateData) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/waiting-rates`, rateData);
        return response.data;
    } catch (error) {
        handleError(error, "Error al guardar tarifa de espera.");
        throw error;
    }
};

export const updateWaitingRate = async (id, rateData) => {
    try {
        const response = await apiClient.put(`${ENDPOINT_PREFIX}/waiting-rates/${id}`, rateData);
        return response.data;
    } catch (error) {
        handleError(error, "Error al actualizar tarifa de espera.");
        throw error;
    }
};

export const deleteWaitingRate = async (id) => {
    try {
        const response = await apiClient.delete(`${ENDPOINT_PREFIX}/waiting-rates/${id}`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al eliminar tarifa de espera.");
        throw error;
    }
};

// --- Bulk Upload ---
export const bulkUploadPassengers = async (clientId, data) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/clients/${clientId}/passengers/bulk-upload`, data);
        return response.data;
    } catch (error) {
        handleError(error, "Error en la carga masiva de pasajeros.");
        throw error;
    }
};

// --- Contracts ---
export const getContracts = async () => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/contracts/`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar contratos.");
        throw error;
    }
};

export const createContract = async (contract) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/contracts/`, contract);
        return response.data;
    } catch (error) {
        handleError(error, "Error al crear contrato.");
        throw error;
    }
};

export const updateContract = async (id, contract) => {
    try {
        const response = await apiClient.put(`${ENDPOINT_PREFIX}/contracts/${id}`, contract);
        return response.data;
    } catch (error) {
        handleError(error, "Error al actualizar contrato.");
        throw error;
    }
};

// --- Zones ---
// --- Zone Groups ---
export const getZoneGroups = async () => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/zone-groups/`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar grupos de zonas.");
        throw error;
    }
};

export const createZoneGroup = async (group) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/zone-groups/`, group);
        return response.data;
    } catch (error) {
        handleError(error, "Error al crear grupo de zonas.");
        throw error;
    }
};

export const updateZoneGroup = async (id, group) => {
    try {
        const response = await apiClient.put(`${ENDPOINT_PREFIX}/zone-groups/${id}`, group);
        return response.data;
    } catch (error) {
        handleError(error, "Error al actualizar grupo de zonas.");
        throw error;
    }
};

export const deleteZoneGroup = async (id) => {
    try {
        const response = await apiClient.delete(`${ENDPOINT_PREFIX}/zone-groups/${id}`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al eliminar grupo de zonas.");
        throw error;
    }
};

export const getZones = async () => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/zones/`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar zonas.");
        throw error;
    }
};

export const createZone = async (zone) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/zones/`, zone);
        return response.data;
    } catch (error) {
        handleError(error, "Error al crear zona.");
        throw error;
    }
};

export const updateZone = async (id, zone) => {
    try {
        const response = await apiClient.put(`${ENDPOINT_PREFIX}/zones/${id}`, zone);
        return response.data;
    } catch (error) {
        handleError(error, "Error al actualizar zona.");
        throw error;
    }
};

export const deleteZone = async (id) => {
    try {
        const response = await apiClient.delete(`${ENDPOINT_PREFIX}/zones/${id}`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al eliminar zona.");
        throw error;
    }
};

export const deleteZonesBulk = async (zoneIds) => {
    try {
        const response = await apiClient.delete(`${ENDPOINT_PREFIX}/zones/bulk`, {
            data: { zone_ids: zoneIds }
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al eliminar zonas masivamente.");
        throw error;
    }
};

export const previewKmlZones = async (file) => {
    try {
        const formData = new FormData();
        formData.append("file", file);
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/zones/preview-kml`, formData, {
            headers: {
                "Content-Type": "multipart/form-data"
            }
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al procesar vista previa del KML.");
        throw error;
    }
};

export const bulkImportZones = async (payload) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/zones/bulk-import`, payload);
        return response.data;
    } catch (error) {
        handleError(error, "Error al importar zonas masivamente.");
        throw error;
    }
};

// --- Bases Operativas ---
export const getBases = async () => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/bases/`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar bases operativas.");
        throw error;
    }
};

export const createBase = async (baseData) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/bases/`, baseData);
        return response.data;
    } catch (error) {
        handleError(error, "Error al crear base operativa.");
        throw error;
    }
};

export const updateBase = async (id, baseData) => {
    try {
        const response = await apiClient.put(`${ENDPOINT_PREFIX}/bases/${id}`, baseData);
        return response.data;
    } catch (error) {
        handleError(error, "Error al actualizar base operativa.");
        throw error;
    }
};

// --- Booking Auto-Assignment Rules ---
export const getBookingAutoRules = async (params = {}) => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/booking-auto-rules/`, { params });
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar reglas de auto-asignación Booking.");
        throw error;
    }
};

export const createBookingAutoRule = async (ruleData) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/booking-auto-rules/`, ruleData);
        return response.data;
    } catch (error) {
        handleError(error, "Error al crear regla de auto-asignación Booking.");
        throw error;
    }
};

export const updateBookingAutoRule = async (id, ruleData) => {
    try {
        const response = await apiClient.put(`${ENDPOINT_PREFIX}/booking-auto-rules/${id}`, ruleData);
        return response.data;
    } catch (error) {
        handleError(error, "Error al actualizar regla de auto-asignación Booking.");
        throw error;
    }
};

export const deleteBookingAutoRule = async (id) => {
    try {
        const response = await apiClient.delete(`${ENDPOINT_PREFIX}/booking-auto-rules/${id}`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al eliminar regla de auto-asignación Booking.");
        throw error;
    }
};

// --- Exchange Rates ---
export const getExchangeRates = async () => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/exchange-rates/`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar tasas de cambio.");
        throw error;
    }
};

export const createExchangeRate = async (rateData) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/exchange-rates/`, rateData);
        return response.data;
    } catch (error) {
        handleError(error, "Error al crear tasa de cambio.");
        throw error;
    }
};

export const updateExchangeRate = async (id, rateData) => {
    try {
        const response = await apiClient.put(`${ENDPOINT_PREFIX}/exchange-rates/${id}`, rateData);
        return response.data;
    } catch (error) {
        handleError(error, "Error al actualizar tasa de cambio.");
        throw error;
    }
};

export const deleteExchangeRate = async (id) => {
    try {
        const response = await apiClient.delete(`${ENDPOINT_PREFIX}/exchange-rates/${id}`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al eliminar tasa de cambio.");
        throw error;
    }
};

// --- Banks ---
export const getBanks = async () => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/banks/`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar lista de bancos.");
        throw error;
    }
};

// --- Tariffs Endpoints ---
export const getTariffs = async (params = {}) => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/tariffs/`, { params });
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar tarifas.");
        throw error;
    }
};

export const createTariff = async (tariff) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/tariffs/`, tariff);
        return response.data;
    } catch (error) {
        handleError(error, "Error al crear tarifa.");
        throw error;
    }
};

export const updateTariff = async (id, tariff) => {
    try {
        const response = await apiClient.put(`${ENDPOINT_PREFIX}/tariffs/${id}`, tariff);
        return response.data;
    } catch (error) {
        handleError(error, "Error al actualizar tarifa.");
        throw error;
    }
};

export const deleteTariff = async (id) => {
    try {
        const response = await apiClient.delete(`${ENDPOINT_PREFIX}/tariffs/${id}`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al eliminar tarifa.");
        throw error;
    }
};

export const exportTariffsToExcel = async (params) => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/tariffs/export/excel`, {
            params,
            responseType: 'blob'
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al exportar tarifas.");
        throw error;
    }
};

export const bulkUpdateGeniusDiscount = async (data) => {
    try {
        const response = await apiClient.put(`${ENDPOINT_PREFIX}/tariffs/bulk-genius-discount`, data);
        return response.data;
    } catch (error) {
        handleError(error, "Error al actualizar descuentos Genius en lote.");
        throw error;
    }
};

// --- DISTANCE RULES (NUEVO) ---
export const getDistanceRules = async () => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/distance-rules/`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar reglas de distancia.");
        throw error;
    }
};

export const createDistanceRule = async (rule) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/distance-rules/`, rule);
        return response.data;
    } catch (error) {
        handleError(error, "Error al crear regla de distancia.");
        throw error;
    }
};

export const updateDistanceRule = async (id, rule) => {
    try {
        const response = await apiClient.put(`${ENDPOINT_PREFIX}/distance-rules/${id}`, rule);
        return response.data;
    } catch (error) {
        handleError(error, "Error al actualizar regla de distancia.");
        throw error;
    }
};

export const deleteDistanceRule = async (id) => {
    try {
        const response = await apiClient.delete(`${ENDPOINT_PREFIX}/distance-rules/${id}`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al eliminar regla de distancia.");
        throw error;
    }
};

// --- Price Rules ---
export const getPriceRules = async () => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/price-rules/`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar reglas de precio.");
        throw error;
    }
};

export const createPriceRule = async (rule) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/price-rules/`, rule);
        return response.data;
    } catch (error) {
        handleError(error, "Error al crear regla de precio.");
        throw error;
    }
};

export const updatePriceRule = async (id, rule) => {
    try {
        const response = await apiClient.put(`${ENDPOINT_PREFIX}/price-rules/${id}`, rule);
        return response.data;
    } catch (error) {
        handleError(error, "Error al actualizar regla de precio.");
        throw error;
    }
};

export const deletePriceRule = async (id) => {
    try {
        const response = await apiClient.delete(`${ENDPOINT_PREFIX}/price-rules/${id}`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al eliminar regla de precio.");
        throw error;
    }
};

// --- Min Price Rules (Booking.com) ---
export const getMinPriceRules = async () => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/min-price-rules/`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar reglas de precio mínimo.");
        throw error;
    }
};

export const createMinPriceRule = async (rule) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/min-price-rules/`, rule);
        return response.data;
    } catch (error) {
        handleError(error, "Error al crear regla de precio mínimo.");
        throw error;
    }
};

export const updateMinPriceRule = async (id, rule) => {
    try {
        const response = await apiClient.put(`${ENDPOINT_PREFIX}/min-price-rules/${id}`, rule);
        return response.data;
    } catch (error) {
        handleError(error, "Error al actualizar regla de precio mínimo.");
        throw error;
    }
};

export const deleteMinPriceRule = async (id) => {
    try {
        const response = await apiClient.delete(`${ENDPOINT_PREFIX}/min-price-rules/${id}`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al eliminar regla de precio mínimo.");
        throw error;
    }
};



// --- Pricing Engine ---
export const getQuote = async (payload) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/pricing/quote`, payload);
        return response.data;
    } catch (error) {
        console.warn("Pricing quote failed", error);
        return [];
    }
};


// --- Holdings / Conglomerados ---
export const getHoldings = async () => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/holdings/`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar holdings.");
        return [];
    }
};

export const createHolding = async (payload) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/holdings/`, payload);
        return response.data;
    } catch (error) {
        handleError(error, "Error al crear holding.");
        throw error;
    }
};

// --- Invitations ---
export const inviteUser = async (inviteData) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/invite-user`, inviteData);
        return response.data;
    } catch (error) {
        handleError(error, "Error al invitar usuario.");
        throw error;
    }
};

// --- Logs ---
export const getTripLogs = async (tripId) => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/trips/${tripId}/logs`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar historial.");
        throw error;
    }
};

// --- Vehicles (NUEVO) ---
export const getVehicles = async (params = {}) => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/vehicles/`, { params });
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar vehículos.");
        throw error;
    }
};

export const getOccupiedVehicleCodes = async () => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/vehicles/occupied-codes`);
        return response.data || [];
    } catch (error) {
        console.error("Error al obtener códigos ocupados:", error);
        return [];
    }
};

export const createVehicle = async (vehicle) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/vehicles/`, vehicle);
        return response.data;
    } catch (error) {
        handleError(error, "Error al crear vehículo.");
        throw error;
    }
};

export const updateVehicle = async (id, vehicle) => {
    try {
        const response = await apiClient.put(`${ENDPOINT_PREFIX}/vehicles/${id}`, vehicle);
        return response.data;
    } catch (error) {
        handleError(error, "Error al actualizar vehículo.");
        throw error;
    }
};

export const deleteVehicle = async (id) => {
    try {
        const response = await apiClient.delete(`${ENDPOINT_PREFIX}/vehicles/${id}`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al eliminar vehículo.");
        throw error;
    }
};

// --- Settlement Groups ---
export const getSettlementGroups = async () => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/settlement-groups/`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar grupos de liquidación.");
        throw error;
    }
};

export const createSettlementGroup = async (group) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/settlement-groups/`, group);
        return response.data;
    } catch (error) {
        handleError(error, "Error al crear grupo de liquidación.");
        throw error;
    }
};

// --- Settlements ---
export const previewSettlement = async (startDate, endDate, groupId, vehicleId, providerId = null) => {
    try {
        const params = { start_date: startDate, end_date: endDate };
        if (groupId) params.settlement_group_id = groupId;
        if (vehicleId) params.vehicle_id = vehicleId;
        if (providerId) params.provider_id = providerId;
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/settlements/preview`, { params });
        return response.data;
    } catch (error) {
        handleError(error, "Error al previsualizar liquidación.");
        throw error;
    }
};

export const generateSettlement = async (startDate, endDate, groupId, vehicleId, driverIds = null, providerId = null) => {
    try {
        const params = { start_date: startDate, end_date: endDate };
        if (groupId) params.settlement_group_id = groupId;
        if (vehicleId) params.vehicle_id = vehicleId;
        if (providerId) params.provider_id = providerId;
        if (driverIds && driverIds.length > 0) params.driver_ids = driverIds.join(',');
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/settlements/generate`, null, { params });
        return response.data;
    } catch (error) {
        handleError(error, "Error al generar liquidación.");
        throw error;
    }
};

export const getSettlements = async (params = {}) => {
    try {
        const cleanParams = { limit: 5000, ...params };
        Object.keys(cleanParams).forEach(k => {
            if (cleanParams[k] === undefined || cleanParams[k] === null || cleanParams[k] === '') {
                delete cleanParams[k];
            }
        });
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/settlements/`, { params: cleanParams });
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar historial de liquidaciones.");
        throw error;
    }
};

export const getSettlementTrips = async (settlementId) => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/settlements/${settlementId}/trips`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar detalle de liquidación.");
        throw error;
    }
};

export const bulkImportTrips = async (formData) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/trips/bulk-import`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al importar viajes masivamente.");
        throw error;
    }
};



export const bulkImportPreview = async (formData) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/trips/bulk-import/preview`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al analizar el archivo.");
        throw error;
    }
};



// --- Trip Audit & Lens ---
export const getTripPositions = async (tripId) => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/trips/${tripId}/positions`);
        return response.data;
    } catch (error) {
        console.error("Error al cargar posiciones GPS.");
        return [];
    }
};

export const getTripEvidence = async (tripId) => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/trips/${tripId}/evidence`);
        return response.data;
    } catch (error) {
        console.error("Error al cargar evidencias Lens.");
        return [];
    }
};

// --- Settlements & Advances Payments ---
export const markSettlementPaid = async (settlementId, invoiceNumber) => {
    try {
        const params = invoiceNumber ? { invoice_number: invoiceNumber } : {};
        const response = await apiClient.put(`${ENDPOINT_PREFIX}/settlements/${settlementId}/mark-paid`, null, { params });
        return response.data;
    } catch (error) {
        handleError(error, "Error al marcar liquidación como pagada.");
        throw error;
    }
};

export const voidSettlement = async (settlementId) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/settlements/${settlementId}/void`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al anular liquidación.");
        throw error;
    }
};

export const uploadSettlementInvoice = async (settlementId, file) => {
    try {
        const formData = new FormData();
        formData.append("file", file);
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/settlements/${settlementId}/invoice`, formData, {
            headers: {
                "Content-Type": "multipart/form-data"
            }
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al subir la factura.");
        throw error;
    }
};

export const getAvailableAdvance = async (driverId, startDate, endDate) => {
    try {
        const params = { start_date: startDate, end_date: endDate };
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/drivers/${driverId}/available-advance`, { params });
        return response.data;
    } catch (error) {
        handleError(error, "Error al obtener monto disponible para anticipo.");
        throw error;
    }
};

export const getAdvances = async () => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/advances`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar anticipos.");
        throw error;
    }
};

export const getSettlementAdvances = async (settlementId) => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/settlements/${settlementId}/advances`);
        return response.data || [];
    } catch (error) {
        console.error("Error al cargar anticipos de la liquidación:", error);
        return [];
    }
};

export const createAdvance = async (advanceData) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/advances`, advanceData);
        return response.data;
    } catch (error) {
        handleError(error, "Error al crear anticipo.");
        throw error;
    }
};

export const markAdvancePaid = async (advanceId, invoiceNumber) => {
    try {
        const params = invoiceNumber ? { invoice_number: invoiceNumber } : {};
        const response = await apiClient.put(`${ENDPOINT_PREFIX}/advances/${advanceId}/mark-paid`, null, { params });
        return response.data;
    } catch (error) {
        handleError(error, "Error al marcar anticipo como pagado.");
        throw error;
    }
};

export const toggleAdvancePause = async (advanceId) => {
    try {
        const response = await apiClient.put(`${ENDPOINT_PREFIX}/advances/${advanceId}/toggle-pause`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al pausar/reactivar el descuento.");
        throw error;
    }
};

export const deleteAdvance = async (advanceId) => {
    try {
        const response = await apiClient.delete(`${ENDPOINT_PREFIX}/advances/${advanceId}`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al eliminar el anticipo/descuento.");
        throw error;
    }
};

// --- Buscador Histórico Anual ---
export const searchHistoricalTrips = async (params = {}) => {
    try {
        const response = await apiClient.get('/transport/trips/historical-search/', { params });
        return response.data;
    } catch (error) {
        handleError(error, "Error al consultar el histórico de servicios.");
        throw error;
    }
};

// --- Booking Simulator ---
export const testBookingFlow = async () => {
    try {
        const response = await apiClient.get('/booking/test-flow');
        return response.data;
    } catch (error) {
        handleError(error, "Error en flujo de prueba Booking.");
        throw error;
    }
};

export const getBookingSearchResults = async (payload) => {
    try {
        const response = await apiClient.post('/booking/v2/search-results', payload);
        return response.data;
    } catch (error) {
        handleError(error, "Error al cotizar tarifas Booking.");
        throw error;
    }
};

export const getSandboxBookings = async () => {
    try {
        const response = await apiClient.get('/booking/sandbox-bookings');
        return response.data;
    } catch (error) {
        handleError(error, "Error al obtener reservas del Sandbox.");
        throw error;
    }
};

export const getBookingAnalytics = async (params = {}) => {
    try {
        const response = await apiClient.get('/booking/analytics', { params });
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar analítica de Booking.com.");
        throw error;
    }
};

export const getBookingProviderDemand = async (params = {}) => {
    try {
        const response = await apiClient.get('/booking/analytics/provider-demand', { params });
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar la analítica de demanda de proveedores de Booking.com.");
        throw error;
    }
};

export const exportBookingSearchLogs = async (params = {}) => {
    try {
        const response = await apiClient.get('/booking/search-logs/export', { 
            params,
            responseType: 'blob'
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al exportar logs de búsqueda de Booking.com.");
        throw error;
    }
};

// --- Providers ---
export const getProviders = async (params = {}) => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/providers/`, { params });
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar proveedores.");
        throw error;
    }
};

export const createProvider = async (provider) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/providers/`, provider);
        return response.data;
    } catch (error) {
        handleError(error, "Error al crear proveedor.");
        throw error;
    }
};

export const updateProvider = async (id, provider) => {
    try {
        const response = await apiClient.put(`${ENDPOINT_PREFIX}/providers/${id}`, provider);
        return response.data;
    } catch (error) {
        handleError(error, "Error al actualizar proveedor.");
        throw error;
    }
};

export const deleteProvider = async (id) => {
    try {
        const response = await apiClient.delete(`${ENDPOINT_PREFIX}/providers/${id}`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al eliminar proveedor.");
        throw error;
    }
};

export const seedProviderZeroTariffs = async (providerId, payload) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/providers/${providerId}/seed-zero-tariffs`, payload);
        return response.data;
    } catch (error) {
        handleError(error, "Error al generar rutas en $0 para el proveedor.");
        throw error;
    }
};

export const importHistoricalContracts = async (formData) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/drivers/historical-contracts/import`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al importar contratos históricos.");
        throw error;
    }
};

export const getWhatsAppChats = async (limit = 50, offset = 0, q = null) => {
    try {
        const params = { limit, offset };
        if (q && String(q).trim()) {
            params.q = String(q).trim();
        }
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/whatsapp/chats`, {
            params
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar conversaciones de WhatsApp.");
        throw error;
    }
};

export const getWhatsAppUnreadCount = async () => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/whatsapp/unread-count`);
        return response.data;
    } catch (error) {
        console.warn("Error al consultar no leídos de WhatsApp:", error);
        return { unread_count: 0 };
    }
};

export const markAllWhatsAppAsRead = async () => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/whatsapp/mark-all-read`);
        return response.data;
    } catch (error) {
        console.warn("Error al marcar todos los chats como leídos:", error);
        throw error;
    }
};

export const resolveWhatsAppQuote = async (phoneNumber) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/whatsapp/chats/${encodeURIComponent(phoneNumber)}/resolve-quote`);
        return response.data;
    } catch (error) {
        console.warn("Error al resolver cotización de WhatsApp:", error);
        return { status: "error" };
    }
};

export const markWhatsAppChatAsUnread = async (phoneNumber) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/whatsapp/chats/${encodeURIComponent(phoneNumber)}/mark-unread`);
        return response.data;
    } catch (error) {
        console.warn("Error al marcar chat como no leído:", error);
        throw error;
    }
};

export const resetWhatsAppChatAI = async (phoneNumber) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/whatsapp/chats/${encodeURIComponent(phoneNumber)}/reset-ai`);
        return response.data;
    } catch (error) {
        console.warn("Error al reiniciar IA del chat:", error);
        throw error;
    }
};

export const acceptWhatsAppChat = async (phoneNumber, operatorName) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/whatsapp/chats/${encodeURIComponent(phoneNumber)}/accept`, {
            operator_name: operatorName
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al aceptar conversación.");
        throw error;
    }
};

export const resolveWhatsAppChat = async (phoneNumber, operatorName) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/whatsapp/chats/${encodeURIComponent(phoneNumber)}/resolve`, {
            operator_name: operatorName
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al resolver conversación.");
        throw error;
    }
};

export const resolveAllWhatsAppChats = async (operatorName) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/whatsapp/chats/resolve-all`, {
            operator_name: operatorName
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al resolver todas las conversaciones.");
        throw error;
    }
};

export const reassignWhatsAppChat = async (phoneNumber, operatorName, targetOperator) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/whatsapp/chats/${encodeURIComponent(phoneNumber)}/reassign`, {
            operator_name: operatorName,
            target_operator: targetOperator
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al reasignar conversación.");
        throw error;
    }
};

export const getWhatsAppMessages = async (phoneNumber, limit = 50, beforeId = null, operatorName = null) => {
    try {
        const params = { limit };
        if (beforeId) params.before_id = beforeId;
        if (operatorName) params.operator_name = operatorName;
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/whatsapp/chats/${encodeURIComponent(phoneNumber)}/messages`, {
            params
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar mensajes del chat.");
        throw error;
    }
};

export const getWhatsAppChatTrips = async (phoneNumber) => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/whatsapp/chats/${encodeURIComponent(phoneNumber)}/trips`);
        return response.data;
    } catch (error) {
        console.error("Error al cargar viajes del chat:", error);
        return [];
    }
};

export const sendWhatsAppFreeText = async (phoneNumber, content, tripId = null, senderName = null) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/whatsapp/messages/send`, {
            phone_number: phoneNumber,
            content,
            trip_id: tripId,
            sender_name: senderName
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al enviar mensaje libre.");
        throw error;
    }
};

export const sendWhatsAppImage = async (phoneNumber, file, caption = "", tripId = null, senderName = null) => {
    try {
        const formData = new FormData();
        formData.append("phone_number", phoneNumber);
        formData.append("caption", caption || "");
        if (tripId !== null && tripId !== undefined) {
            formData.append("trip_id", String(tripId));
        }
        if (senderName) {
            formData.append("sender_name", senderName);
        }
        formData.append("file", file, file.name || "imagen.png");

        const response = await apiClient.post(`${ENDPOINT_PREFIX}/whatsapp/messages/send-image`, formData, {
            headers: { "Content-Type": "multipart/form-data" }
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al enviar imagen por WhatsApp.");
        throw error;
    }
};

export const sendWhatsAppTemplate = async (phoneNumber, templateName, parameters = [], tripId = null, languageCode = "es", senderName = null) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/whatsapp/messages/send-template`, {
            phone_number: phoneNumber,
            template_name: templateName,
            language_code: languageCode,
            parameters,
            trip_id: tripId,
            sender_name: senderName
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al enviar plantilla.");
        throw error;
    }
};

export const searchWhatsAppContacts = async (query) => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/whatsapp/contacts/search`, {
            params: { q: query }
        });
        return response.data || [];
    } catch (error) {
        console.error("Error buscando contactos WhatsApp:", error);
        return [];
    }
};

export const translateWhatsAppText = async (text, targetLanguage = "es", phoneNumber = null) => {
    const cleanInput = String(text || "")
        .replace(/\[MEDIA_ID:[^\]]+\]\s*/g, "")
        .replace(/\[MAP_URL:[^\r\n]+\]\s*/g, "")
        .replace(/\[AI_OCR\][\s\S]*?\[\/AI_OCR\]\s*/g, "")
        .trim() || String(text || "").trim();

    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/whatsapp/translate`, {
            text: cleanInput,
            target_language: targetLanguage,
            phone_number: phoneNumber
        });
        return response.data;
    } catch (error) {
        // Fallback instantáneo en navegador si el backend está redesplegando (404) o falla Gemini
        try {
            const langNames = {
                es: "Español",
                en: "Inglés",
                pt: "Portugués",
                fr: "Francés",
                de: "Alemán",
                it: "Italiano"
            };
            let resolvedTarget = (targetLanguage || "es").toLowerCase();
            if (resolvedTarget === "auto_passenger") {
                const digits = String(phoneNumber || "").replace(/\D/g, "");
                if (digits.startsWith("55") || digits.startsWith("351")) resolvedTarget = "pt";
                else if (digits.startsWith("33") || digits.startsWith("32") || digits.startsWith("41")) resolvedTarget = "fr";
                else if (digits.startsWith("49") || digits.startsWith("43")) resolvedTarget = "de";
                else if (digits.startsWith("39")) resolvedTarget = "it";
                else resolvedTarget = "en";
            }
            const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=auto&tl=${encodeURIComponent(resolvedTarget)}&dt=t&q=${encodeURIComponent(cleanInput)}`;
            const res = await fetch(url);
            if (res.ok) {
                const data = await res.json();
                const translated = Array.isArray(data?.[0])
                    ? data[0].map((part) => part?.[0] || "").join("").trim()
                    : "";
                const detectedCode = String(data?.[2] || "").toLowerCase();
                if (translated) {
                    return {
                        status: "success",
                        original_text: cleanInput,
                        translated_text: translated,
                        detected_source_language: langNames[detectedCode] || (detectedCode ? detectedCode.toUpperCase() : ""),
                        target_language_name: langNames[resolvedTarget] || resolvedTarget.toUpperCase()
                    };
                }
            }
        } catch (fallbackErr) {
            console.warn("Fallback translator error:", fallbackErr);
        }
        handleError(error, "Error al traducir el mensaje.");
        throw error;
    }
};

export const bulkSendPassengerConfirmations = async (tripIds, recipientKeys = null, templateName = "recordatorio_pasajero_conductor", languageCode = "es") => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/whatsapp/trips/bulk-send-passenger-confirmations`, {
            trip_ids: tripIds,
            recipient_keys: recipientKeys,
            template_name: templateName,
            language_code: languageCode
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al enviar confirmaciones masivas.");
        throw error;
    }
};

export const sendDriverDailySchedule = async (driverId, dateStr) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/whatsapp/drivers/send-daily-schedule`, {
            driver_id: driverId,
            date_str: dateStr
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al enviar agenda diaria al chofer.");
        throw error;
    }
};

export const getUniqueVouchers = async (startDate, endDate) => {
    try {
        const params = {};
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/insights/filters/vouchers`, { params });
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar vouchers únicos.");
        throw error;
    }
};

export const getDispatcherDispersionInsights = async (params) => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/insights/dispatcher-dispersion`, { params });
        return response.data;
    } catch (error) {
        handleError(error, "Error al obtener KPI de dispersión de locutoras.");
        throw error;
    }
};

export const getEvaAiInsights = async (params) => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/insights/eva-ai`, { params });
        return response.data;
    } catch (error) {
        handleError(error, "Error al obtener métricas de Rendimiento Eva (IA).");
        throw error;
    }
};

export const getMarginRules = async () => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/margin-rules`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar reglas de margen.");
        throw error;
    }
};

export const createMarginRule = async (rule) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/margin-rules`, rule);
        return response.data;
    } catch (error) {
        handleError(error, "Error al crear regla de margen.");
        throw error;
    }
};

export const updateMarginRule = async (id, rule) => {
    try {
        const response = await apiClient.put(`${ENDPOINT_PREFIX}/margin-rules/${id}`, rule);
        return response.data;
    } catch (error) {
        handleError(error, "Error al actualizar regla de margen.");
        throw error;
    }
};

export const deleteMarginRule = async (id) => {
    try {
        const response = await apiClient.delete(`${ENDPOINT_PREFIX}/margin-rules/${id}`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al eliminar regla de margen.");
        throw error;
    }
};

export const getTariffChangeLogs = async () => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/tariff-change-logs`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar el historial de cambios de costo.");
        throw error;
    }
};

export const getMinPriceChangeLogs = async () => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/min-price-change-logs`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar el historial de cambios de mínimos.");
        throw error;
    }
};

export const getDriverNotificationLogs = async (params = {}) => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/drivers/notifications/logs`, { params });
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar la bitácora de notificaciones.");
        throw error;
    }
};

export const triggerTomorrowAgendaNotifications = async () => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/drivers/notifications/trigger-tomorrow-agenda`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al disparar el proceso de notificaciones de agenda.");
        throw error;
    }
};

export const getPendingConfirmationsCount = async () => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/drivers/notifications/pending-count`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al obtener conteo de notificaciones pendientes.");
        throw error;
    }
};

export const resendDriverNotification = async (driverId, dateStr) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/drivers/notifications/resend`, {
            driver_id: driverId,
            date: dateStr
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al re-enviar la notificación push.");
        throw error;
    }
};

export const getTomorrowAcknowledgment = async () => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/drivers/notifications/tomorrow-acknowledgment`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al obtener estado de confirmación de agenda.");
        throw error;
    }
};

export const acknowledgeTomorrowAgenda = async (dateStr) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/drivers/notifications/acknowledge`, {
            date: dateStr
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al confirmar lectura de agenda.");
        throw error;
    }
};

export const getQualityInsights = async (params) => {
    try {
        const response = await apiClient.get('/booking/v2/quality/insights', { params });
        return response.data;
    } catch (error) {
        handleError(error, "Error al obtener insights de calidad.");
        throw error;
    }
};

export const updateSurveyManagement = async (tripId, data) => {
    try {
        const response = await apiClient.patch(`/booking/v2/quality/surveys/${tripId}`, data);
        return response.data;
    } catch (error) {
        handleError(error, "Error al actualizar gestión de la encuesta.");
        throw error;
    }
};

export const syncBookingSurveys = async (days = 7) => {
    try {
        const response = await apiClient.post('/booking/v2/surveys/sync', null, { params: { days } });
        return response.data;
    } catch (error) {
        handleError(error, "Error al sincronizar encuestas con Booking.com.");
        throw error;
    }
};

export const updateIncidentManagement = async (tripId, data) => {
    try {
        const response = await apiClient.patch(`/booking/v2/quality/incidents/${tripId}`, data);
        return response.data;
    } catch (error) {
        handleError(error, "Error al actualizar gestión del incidente.");
        throw error;
    }
};

export const getPendingDocuments = async () => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/admin/documents/pending`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al obtener documentos pendientes de aprobación.");
        throw error;
    }
};

export const reviewDocument = async (docId, status, rejectionReason = '', expirationDate = '', internalCode = '', providerId = null) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/admin/documents/${docId}/review`, {
            status,
            rejection_reason: rejectionReason,
            expiration_date: expirationDate,
            internal_code: internalCode,
            provider_id: providerId ? parseInt(providerId) : null
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al procesar la revisión del documento.");
        throw error;
    }
};

export const getDriverDocumentsAdmin = async (driverId) => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/admin/drivers/${driverId}/documents`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al obtener documentos del conductor.");
        throw error;
    }
};

export const uploadDriverDocumentAdmin = async (driverId, documentType, file) => {
    try {
        const formData = new FormData();
        formData.append('document_type', documentType);
        formData.append('file', file);
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/admin/drivers/${driverId}/documents/upload`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al subir el documento del conductor.");
        throw error;
    }
};

export const saveOnboardingVehicle = async (vehicleData) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/drivers/me/vehicle`, vehicleData);
        return response.data;
    } catch (error) {
        handleError(error, "Error al guardar la información del vehículo.");
        throw error;
    }
};

export const checkMobileAvailability = async (code, vehicleId = null) => {
    try {
        const params = vehicleId ? { vehicle_id: vehicleId } : {};
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/vehicles/check-mobile/${encodeURIComponent(code)}`, { params });
        return response.data;
    } catch (error) {
        console.warn("Error al verificar disponibilidad del N° de móvil:", error);
        return { is_available: true, conflict: null, suggestions: [] };
    }
};

export const downloadSmartBulkTemplate = async () => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/smart-bulk/template`, {
            responseType: 'blob'
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al descargar la plantilla de carga inteligente.");
        throw error;
    }
};

export const previewSmartBulk = async (formData) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/smart-bulk/preview`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al procesar la previsualización masiva.");
        throw error;
    }
};

export const confirmSmartBulk = async (payload) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/smart-bulk/confirm`, payload, {
            responseType: 'blob'
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al confirmar las reservas masivas.");
        throw error;
    }
};

export const parseQuoteText = async (text) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/quotes/parse-text`, { text });
        return response.data;
    } catch (error) {
        handleError(error, "Error al procesar el texto de la cotización.");
        throw error;
    }
};

export const getQuotes = async (params = {}) => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/quotes/`, { params });
        return response.data;
    } catch (error) {
        handleError(error, "Error al obtener las cotizaciones.");
        throw error;
    }
};

export const getQuoteById = async (id) => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/quotes/${id}`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al obtener la cotización.");
        throw error;
    }
};

export const createQuote = async (data) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/quotes/`, data);
        return response.data;
    } catch (error) {
        handleError(error, "Error al crear la cotización.");
        throw error;
    }
};

export const updateQuote = async (id, data) => {
    try {
        const response = await apiClient.put(`${ENDPOINT_PREFIX}/quotes/${id}`, data);
        return response.data;
    } catch (error) {
        handleError(error, "Error al actualizar la cotización.");
        throw error;
    }
};

export const duplicateQuote = async (id) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/quotes/${id}/duplicate`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al duplicar la cotización.");
        throw error;
    }
};

export const convertQuoteToTrips = async (id) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/quotes/${id}/convert-to-trips`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al convertir cotización en viajes.");
        throw error;
    }
};

// --- Disponibilidad y Horarios de Choferes (EmiGo) ---
export const getDriverSchedule = async (driverId = null) => {
    try {
        const url = driverId ? `${ENDPOINT_PREFIX}/drivers/${driverId}/schedule` : `${ENDPOINT_PREFIX}/drivers/me/schedule`;
        const response = await apiClient.get(url);
        return response.data;
    } catch (error) {
        handleError(error, "Error al obtener horario y disponibilidad.");
        throw error;
    }
};

export const updateDriverSchedule = async (weeklySchedule, driverId = null) => {
    try {
        const url = driverId ? `${ENDPOINT_PREFIX}/drivers/${driverId}/schedule` : `${ENDPOINT_PREFIX}/drivers/me/schedule`;
        const response = await apiClient.put(url, { weekly_schedule: weeklySchedule });
        return response.data;
    } catch (error) {
        handleError(error, "Error al actualizar horario semanal.");
        throw error;
    }
};

export const checkUnavailabilityConflicts = async (data, driverId = null) => {
    try {
        const url = driverId ? `${ENDPOINT_PREFIX}/drivers/${driverId}/unavailabilities/check-conflicts` : `${ENDPOINT_PREFIX}/drivers/me/unavailabilities/check-conflicts`;
        const response = await apiClient.post(url, data);
        return response.data;
    } catch (error) {
        handleError(error, "Error al verificar conflictos de agenda.");
        throw error;
    }
};

export const createDriverUnavailability = async (data, driverId = null) => {
    try {
        const url = driverId ? `${ENDPOINT_PREFIX}/drivers/${driverId}/unavailabilities` : `${ENDPOINT_PREFIX}/drivers/me/unavailabilities`;
        const response = await apiClient.post(url, data);
        return response.data;
    } catch (error) {
        handleError(error, "Error al programar ausencia.");
        throw error;
    }
};

export const deleteDriverUnavailability = async (id, driverId = null) => {
    try {
        const url = driverId ? `${ENDPOINT_PREFIX}/drivers/${driverId}/unavailabilities/${id}` : `${ENDPOINT_PREFIX}/drivers/me/unavailabilities/${id}`;
        const response = await apiClient.delete(url);
        return response.data;
    } catch (error) {
        handleError(error, "Error al eliminar período de ausencia.");
        throw error;
    }
};

export const uploadMonthlySettlementInvoice = async (year, month, fileOrBlob, filename = 'monthly_invoice.jpg') => {
    try {
        const formData = new FormData();
        formData.append('year', year);
        formData.append('month', month);
        formData.append('file', fileOrBlob, filename);
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/settlements/monthly-invoice`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al subir factura mensual.");
        throw error;
    }
};

// --- dLocal Go & Transbank Webpay Plus ---
export const createDLocalPaymentLink = async ({ trip_id = null, trip_ids = null, currency = 'CLP', custom_amount = null, provider = null }) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/dlocal/create-payment-link/`, {
            trip_id,
            trip_ids,
            currency,
            custom_amount,
            provider
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al generar link de pago.");
        throw error;
    }
};

export const resetDLocalPayment = async (trip_id) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/dlocal/reset-payment/${trip_id}/`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al resetear pago de prueba.");
        throw error;
    }
};

export const getDLocalPaymentStatus = async (trip_id, silent = false) => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/dlocal/check-payment-status/${trip_id}/`);
        return response.data;
    } catch (error) {
        if (!silent) {
            handleError(error, "Error al consultar estado de pago dLocal.");
        }
        throw error;
    }
};

export const sendDLocalPaymentLinkWhatsApp = async ({ trip_id = null, trip_ids = null, payment_url, amount_str, target_phone = null }) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/dlocal/send-payment-link-whatsapp/`, {
            trip_id,
            trip_ids,
            payment_url,
            amount_str,
            target_phone
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al enviar link de pago por WhatsApp.");
        throw error;
    }
};

// --- Asistente Inteligente de Equidad en Despacho ---
export const getRecommendedDrivers = async (params = {}) => {
    try {
        const queryParams = new URLSearchParams();
        if (params.base_id) queryParams.append('base_id', params.base_id);
        if (params.scheduled_time) queryParams.append('scheduled_time', params.scheduled_time);
        if (params.category) queryParams.append('category', params.category);
        if (params.force_refresh) queryParams.append('force_refresh', params.force_refresh);
        
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/dispatchers/recommended-drivers?${queryParams.toString()}`);
        return response.data;
    } catch (error) {
        console.error("Error al cargar conductores recomendados:", error);
        return { standard_recommendations: [], van_recommendations: [], all_recommendations: [] };
    }
};

// --- Bot Centinela de Booking.com (Mystery Shopper) ---
export const getSentinelRoutes = async (params = {}) => {
    try {
        const response = await apiClient.get('/booking/sentinel/routes', { params });
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar las rutas del Centinela.");
        throw error;
    }
};

export const createSentinelRoute = async (routeData) => {
    try {
        const response = await apiClient.post('/booking/sentinel/routes', routeData);
        return response.data;
    } catch (error) {
        handleError(error, "Error al crear la ruta en el Centinela.");
        throw error;
    }
};

export const updateSentinelRoute = async (routeId, routeData) => {
    try {
        const response = await apiClient.put(`/booking/sentinel/routes/${routeId}`, routeData);
        return response.data;
    } catch (error) {
        handleError(error, "Error al actualizar la ruta en el Centinela.");
        throw error;
    }
};

export const deleteSentinelRoute = async (routeId) => {
    try {
        const response = await apiClient.delete(`/booking/sentinel/routes/${routeId}`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al eliminar la ruta del Centinela.");
        throw error;
    }
};

export const applySentinelTariff = async (payload) => {
    try {
        const response = await apiClient.post('/booking/sentinel/apply-tariff', payload);
        return response.data;
    } catch (error) {
        handleError(error, "Error al actualizar la tarifa desde el Centinela.");
        throw error;
    }
};

export const getSentinelLogs = async (params = {}) => {
    try {
        const response = await apiClient.get('/booking/sentinel/logs', { params });
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar el historial del Centinela.");
        throw error;
    }
};

export const triggerSentinelCheck = async (payload = {}) => {
    try {
        const response = await apiClient.post('/booking/sentinel/trigger-check', payload);
        return response.data;
    } catch (error) {
        handleError(error, "Error al solicitar la auditoría del Centinela.");
        throw error;
    }
};

// --- Puntos de Encuentro Booking.com (Pickup Instructions) ---
export const getBookingMeetingPoints = async (params = {}) => {
    try {
        const response = await apiClient.get('/booking/meeting-points', { params });
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar los puntos de encuentro de Booking.com.");
        throw error;
    }
};

export const createBookingMeetingPoint = async (data) => {
    try {
        const response = await apiClient.post('/booking/meeting-points', data);
        return response.data;
    } catch (error) {
        handleError(error, "Error al crear el punto de encuentro.");
        throw error;
    }
};

export const updateBookingMeetingPoint = async (pointId, data) => {
    try {
        const response = await apiClient.put(`/booking/meeting-points/${pointId}`, data);
        return response.data;
    } catch (error) {
        handleError(error, "Error al actualizar el punto de encuentro.");
        throw error;
    }
};

export const deleteBookingMeetingPoint = async (pointId) => {
    try {
        const response = await apiClient.delete(`/booking/meeting-points/${pointId}`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al eliminar el punto de encuentro.");
        throw error;
    }
};

export const sendTripBookingMeetingPoint = async (tripId, meetingPointId = null) => {
    try {
        const params = meetingPointId ? { meeting_point_id: meetingPointId } : {};
        const response = await apiClient.post(`/booking/trips/${tripId}/send-meeting-point`, null, { params });
        return response.data;
    } catch (error) {
        handleError(error, "Error al enviar el punto de encuentro a Booking.com.");
        throw error;
    }
};

export const uploadBookingMeetingPointImage = async (file) => {
    try {
        const formData = new FormData();
        formData.append('file', file);
        const response = await apiClient.post('/booking/meeting-points/upload-image', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al subir la fotografía del punto de encuentro.");
        throw error;
    }
};

// --- Booking.com Live Chat (Conductor <-> Pasajero & Locutoras) ---
export const getTripBookingChatMessages = async (tripId, syncRemote = false, viewerRole = "driver") => {
    try {
        const response = await apiClient.get(`/booking/trips/${tripId}/chat-messages`, {
            params: { sync_api: syncRemote, viewer_role: viewerRole }
        });
        return response.data;
    } catch (error) {
        console.warn("Error al cargar mensajes de Booking Live Chat:", error);
        throw error;
    }
};

export const sendTripBookingChatMessage = async (tripId, content, senderRole = "DRIVER", senderName = null) => {
    try {
        const response = await apiClient.post(`/booking/trips/${tripId}/chat-messages`, {
            message: content,
            content,
            sender_role: senderRole,
            sender_name: senderName
        });
        return response.data;
    } catch (error) {
        handleError(error, "Error al enviar mensaje por Booking Live Chat.");
        throw error;
    }
};

export const getDriverUnreadBookingChats = async (driverId) => {
    try {
        const response = await apiClient.get(`/booking/driver/${driverId}/unread-chats`);
        return response.data;
    } catch (error) {
        console.warn("Error al consultar chats no leídos de Booking:", error);
        return { total_unread: 0, trips_unread: {}, unread_by_trip: {} };
    }
};

// --- TRANSFEERO + MOVIO FREE (CENTINELA HUMANIZADO & MANTENEDOR) ---
export const getTransfeeroConfig = async () => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/transfeero/config`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar la configuración de Transfeero.");
        throw error;
    }
};

export const updateTransfeeroConfig = async (payload) => {
    try {
        const response = await apiClient.put(`${ENDPOINT_PREFIX}/transfeero/config`, payload);
        return response.data;
    } catch (error) {
        handleError(error, "Error al guardar la configuración de Transfeero.");
        throw error;
    }
};

export const syncTransfeeroNow = async () => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/transfeero/sync-now`);
        return response.data;
    } catch (error) {
        handleError(error, "Error al sincronizar reservas de Transfeero desde Movio.");
        throw error;
    }
};

export const simulateTransfeeroOffer = async (payload) => {
    try {
        const response = await apiClient.post(`${ENDPOINT_PREFIX}/transfeero/simulate`, payload);
        return response.data;
    } catch (error) {
        handleError(error, "Error al simular la oferta de Transfeero.");
        throw error;
    }
};

export const getTransfeeroLogs = async (params = {}) => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/transfeero/logs`, { params });
        return response.data;
    } catch (error) {
        handleError(error, "Error al cargar el historial de ofertas de Transfeero.");
        throw error;
    }
};

export const downloadTransfeeroExtensionZip = async () => {
    try {
        const response = await apiClient.get(`${ENDPOINT_PREFIX}/transfeero/extension-download`, {
            responseType: 'blob'
        });
        const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', 'emigo-transfeero-sentinel.zip');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
        return true;
    } catch (error) {
        handleError(error, "Error al descargar la extensión Chrome de Transfeero.");
        throw error;
    }
};

